/**
 * Manual reception viewer + delete confirmation.
 *
 * Two presentational dialogs extracted from OrdersDialogs.jsx:
 *  - <ManualReceptionDetailDialog /> : read-only view of a manual reception
 *    with action buttons (Close / Duplicate / Edit) routed via callbacks.
 *  - <DeleteManualReceptionAlert /> : AlertDialog confirming deletion.
 *
 * Both are pure presentational; all state lives in the parent (OrdersTab).
 */
import React from 'react';
import { Copy, Pencil } from 'lucide-react';
import { formatCurrency, formatDate } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogTitle,
} from '@/components/ui/dialog';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';

export function ManualReceptionDetailDialog({
    open,
    onOpenChange,
    manualReception,
    onDuplicate,
    onEdit,
}) {
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[95vw] sm:w-full sm:h-[95vh] overflow-hidden flex flex-col">
                {/* Compact header on one line */}
                <div className="flex-shrink-0 flex items-center gap-4 pb-2 border-b flex-wrap pr-12 sm:pr-14">
                    <DialogTitle className="text-lg font-semibold whitespace-nowrap">
                        {manualReception?.reception_number}
                    </DialogTitle>
                    <DialogDescription className="sr-only">
                        Détail de la réception manuelle {manualReception?.reception_number || ''}
                    </DialogDescription>
                    {manualReception && (
                        <div className="flex items-center gap-4 text-xs flex-1 min-w-0 flex-wrap">
                            <span className="text-slate-500">{manualReception.supplier_name}</span>
                            <span className="text-slate-400">|</span>
                            <span className="text-slate-500">{manualReception.site_name}</span>
                            <span className="text-slate-400">|</span>
                            <span className="text-slate-500">{formatDate(manualReception.reception_date || manualReception.created_at)}</span>
                            {manualReception.document_number && (
                                <>
                                    <span className="text-slate-400">|</span>
                                    <span className="text-slate-500">{manualReception.document_number}</span>
                                </>
                            )}
                            <span className="text-slate-400">|</span>
                            <span className="font-bold text-emerald-600">{formatCurrency(manualReception.total_amount)}</span>
                        </div>
                    )}
                </div>
                {manualReception && (
                    <div className="flex-1 overflow-auto min-h-0 py-2">
                        <div className="border rounded-lg overflow-hidden">
                            <table className="w-full text-sm">
                                <thead className="bg-slate-50">
                                    <tr>
                                        <th className="text-left p-3">Produit</th>
                                        <th className="text-right p-3">Prix unit.</th>
                                        <th className="text-right p-3">Qté</th>
                                        <th className="text-right p-3">Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {manualReception.items?.map((item, index) => (
                                        <tr key={index} className="border-t">
                                            <td className="p-3">
                                                <div className="flex items-center gap-2">
                                                    <span className="font-mono text-xs text-slate-400 w-24 shrink-0" data-testid={`manual-reception-item-ref-${index}`}>{item.supplier_ref || ''}</span>
                                                    <span>{item.supplier_product_name || item.industrial_product_name || 'Produit'}</span>
                                                </div>
                                            </td>
                                            <td className="p-3 text-right">{formatCurrency(item.unit_price)}</td>
                                            <td className="p-3 text-right">{item.quantity}</td>
                                            <td className="p-3 text-right font-medium">{formatCurrency(item.total)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot className="bg-emerald-50">
                                    <tr>
                                        <td colSpan="3" className="p-3 text-right font-semibold">Total :</td>
                                        <td className="p-3 text-right font-bold text-emerald-700">{formatCurrency(manualReception.total_amount)}</td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>

                        {manualReception.notes && (
                            <div>
                                <p className="text-slate-500 text-sm">Notes</p>
                                <p>{manualReception.notes}</p>
                            </div>
                        )}
                    </div>
                )}
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>
                        Fermer
                    </Button>
                    <Button
                        variant="outline"
                        onClick={() => onDuplicate(manualReception)}
                        className="border-blue-300 text-blue-700 hover:bg-blue-50"
                        data-testid="duplicate-manual-reception-btn"
                    >
                        <Copy className="w-4 h-4 mr-2" />
                        Dupliquer
                    </Button>
                    <Button
                        onClick={() => onEdit(manualReception)}
                        className="bg-emerald-500 hover:bg-emerald-600 text-white"
                        data-testid="edit-manual-reception-btn"
                    >
                        <Pencil className="w-4 h-4 mr-2" />
                        Modifier
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export function DeleteManualReceptionAlert({
    open,
    onOpenChange,
    receptionNumber,
    onConfirm,
}) {
    return (
        <AlertDialog open={open} onOpenChange={onOpenChange}>
            <AlertDialogContent>
                <AlertDialogHeader>
                    <AlertDialogTitle>Supprimer la réception manuelle ?</AlertDialogTitle>
                    <AlertDialogDescription>
                        Êtes-vous sûr de vouloir supprimer la réception {receptionNumber} ?
                        Cette action supprimera également les mouvements de stock associés.
                    </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                    <AlertDialogCancel>Annuler</AlertDialogCancel>
                    <AlertDialogAction onClick={onConfirm} className="bg-red-500 hover:bg-red-600">
                        Supprimer
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
}
